import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APP_TYPE,
  BeatPlanTypeEnum,
  CfaData,
  dispatch,
  ErrorVariants,
  getAppType,
  getCfaPayload,
  getDivisionsByCfaId,
  getIsZoaiCustomerFlowState,
  getNewCustomerPayload,
  getNonPeriodicBeatPlanInfo,
  getPeriodicBeatPlanInfo,
  getSelectedCfas,
  getUserData,
  getWorkspace,
  NewCustomerPayload,
  removeSearchKey,
  SelectedCfa,
  showToast,
  updateExtraDetails,
  updateSelectedCfa,
  updateZoaiCustomerFlowState,
  useAppSelector,
  useCreateNewCustomerMutation,
  useLazyGetCfaListQuery,
  usePublishMessageMutation,
  getWahaConfigId,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { appendCountryCode } from '@zonofi/common/functions/format-phone';
import { FIELD_OPS_ROUTES, MessageType } from '../../../constants';
import {
  addCustomerModalGroupRef,
  useDebounceSearch,
  usePaginatedList,
  useResetFlow,
} from '@zonofi/seller-hub';

const hasRequiredKeys = (payload: NewCustomerPayload) =>
  !!(
    payload?.customerName &&
    payload?.mobileNumber &&
    payload?.firmName &&
    payload?.address
  );

export const useAddCFAs = (swPathList?: string[]) => {
  const navigate = useNavigate();
  const appType = useAppSelector(getAppType);
  const workspace = useAppSelector(getWorkspace);
  const selectedCfas = useAppSelector(getSelectedCfas);
  const newCustomerPayload = useAppSelector(getNewCustomerPayload);
  const cfaPayload = useAppSelector(getCfaPayload);
  const nonPeriodicBeatPlanInfo = useAppSelector(getNonPeriodicBeatPlanInfo);
  const periodicBeatPlanInfo = useAppSelector(getPeriodicBeatPlanInfo);
  const { handleBack } = useResetFlow();

  const [showSearch, setShowSearch] = useState(false);
  const [showDivisionModal, setShowDivisionModal] = useState(false);
  const [currentSelectedCfa, setCurrentSelectedCfa] = useState<SelectedCfa>();
  const { debouncedSearchValue } = useDebounceSearch();

  const [fetchCFAsList, fetchCFAsListResponse] = useLazyGetCfaListQuery();
  const [createCustomer, createCustomerResponse] =
    useCreateNewCustomerMutation();
  const [sendMessage] = usePublishMessageMutation();

  const isZoaiCustomerFlow = useAppSelector(getIsZoaiCustomerFlowState);
  const user = useAppSelector(getUserData);
  const waConfigId = useAppSelector(getWahaConfigId);
  const assignedCfaCount = useAppSelector((state) =>
    selectedCfas.reduce((count, cfa) => {
      const divisions = getDivisionsByCfaId(state, cfa.id);
      return divisions?.length ? count + 1 : count;
    }, 0)
  );

  useEffect(() => {
    return () => {
      dispatch(removeSearchKey());
    };
  }, []);

  const fetchCFAsListApiCall = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      fetchCFAsList({
        workspaceId: String(workspace?.id),
        ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
        ...params,
      });
    },
    [debouncedSearchValue]
  );

  const memoizedCfasData = useMemo(() => {
    return fetchCFAsListResponse.data?.cfas ?? [];
  }, [fetchCFAsListResponse.data?.cfas]);

  const {
    data: cfasData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
  } = usePaginatedList<CfaData>({
    fetchDataApiCall: fetchCFAsListApiCall,
    apiResponseData: memoizedCfasData,
    isLoading: fetchCFAsListResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue],
    isError: fetchCFAsListResponse.isError,
    paginationMeta: {
      startRecord: fetchCFAsListResponse?.data?.startRecord || 0,
      endRecord: fetchCFAsListResponse?.data?.endRecord || 0,
      totalCount: fetchCFAsListResponse?.data?.totalCount || 0,
    },
  });

  const onCfaSelect = (cfa: CfaData) => {
    dispatch(
      updateSelectedCfa({
        id: cfa?.id,
        cfaName: cfa?.supplierName,
        cfaCode: cfa?.fulfillmentLocationCode,
      })
    );
  };

  const onEditOrAssignPress = (cfa: CfaData) => {
    setCurrentSelectedCfa({
      id: cfa?.id,
      cfaName: cfa?.supplierName,
      cfaCode: cfa?.fulfillmentLocationCode,
    });
    setShowDivisionModal(true);
  };

  const onAssignCfaPress = () => {
    if (!hasRequiredKeys(newCustomerPayload)) return;

    createCustomer({
      senderWorkspaceId: workspace?.id,
      phone: appendCountryCode(newCustomerPayload.mobileNumber),
      isSelfRegister: true,
      customerDetails: {
        name: newCustomerPayload.customerName,
        companyName: newCustomerPayload.firmName,
        telephone: appendCountryCode(newCustomerPayload.mobileNumber),
        customerCode: newCustomerPayload.customerCode,
        importSource: 'manual',
        ...(newCustomerPayload.emailId && {
          email: newCustomerPayload.emailId,
        }),
        ...(newCustomerPayload.address && {
          physicalAddress: { address1: newCustomerPayload.address },
        }),
        ...(newCustomerPayload.isSameAddress && {
          shippingAddress: {
            shippingAddress1: newCustomerPayload.address,
            countryCode: 'IN',
          },
        }),
        countryCode: 'IN',
        ...(newCustomerPayload?.location
          ? {
              location: [
                Number(newCustomerPayload.location[0]),
                Number(newCustomerPayload.location[1]),
              ],
            }
          : {}),
      },
      cfa: cfaPayload.filter((item) => item !== null),
    });
  };

  useEffect(() => {
    if (createCustomerResponse.isSuccess && createCustomerResponse.data?.data) {
      const customer = createCustomerResponse.data?.data;
      if (isZoaiCustomerFlow.state) {
        sendMessage({
          sellerWorkspaceId: workspace?.id ?? '',
          channelId: isZoaiCustomerFlow.channel,
          message: {
            _id: uuidv4(),
            text: `✅ New Lead Captured!\n\n🏭 ${customer?.customerDetails?.companyName}\n📍 ${customer?.customerDetails?.physicalAddress?.address1}\n👤 ${customer?.customerDetails?.name} · +${customer?.customerDetails?.telephone}\n👨‍💼 Registered by: ${user.firstName}\n\nLead saved to CRM. Welcome message sent to ${customer?.customerDetails?.name}. 📱`,
            type: MessageType.TEXT,
            sId: user?.id,
            cId: isZoaiCustomerFlow.channel,
            cAt: Date.now(),
            user: {
              firstName: user?.firstName,
              lastName: user?.lastName,
              mobile: user?.mobile,
              email: user?.email,
              id: user?.id,
            },
            chnType: 'WAHA',
            waConfigId,
            source: 'system',
          },
        });
      }
      if (swPathList && swPathList.length)
        navigator.serviceWorker.controller?.postMessage({
          type: 'RECORD_MUTATION',
          listPaths: swPathList,
        });
      dispatch(
        updateExtraDetails({
          customerId: customer?.inviteId || '',
          customerName: customer?.customerDetails?.name || '',
          companyName: customer?.customerDetails?.companyName || '',
          mobile: customer?.customerDetails?.telephone || '',
        })
      );

      setTimeout(() => {
        dispatch(
          showToast({
            message:
              appType === APP_TYPE.FIELDS_OPS
                ? `${assignedCfaCount} CFAs assigned`
                : 'Customer Added Successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
        dispatch(updateZoaiCustomerFlowState({ state: false, channel: '' }));
      }, 100);

      if (appType === APP_TYPE.FIELDS_OPS) {
        const beatType =
          nonPeriodicBeatPlanInfo?.beatType ?? periodicBeatPlanInfo;
        navigate(
          beatType === BeatPlanTypeEnum.Routes
            ? FIELD_OPS_ROUTES.ASSIGN_ROUTES
            : FIELD_OPS_ROUTES.ASSIGN_BEAT_PLAN
        );
        return;
      } else if (appType === APP_TYPE.SELLER_HUB) {
        handleBack();
        setTimeout(() => {
          addCustomerModalGroupRef?.current?.openSuccessModal();
        }, 100);
        return;
      }
    }

    if (createCustomerResponse.isError) {
      dispatch(
        showToast({
          message: 'Failed to add new customer',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [createCustomerResponse]);

  return {
    cfasData,
    showSearch,
    showDivisionModal,
    currentSelectedCfa,
    onCfaSelect,
    onEditOrAssignPress,
    onAssignCfaPress,
    setShowSearch,
    setShowDivisionModal,
    assignedCfaCount,
    customerName: workspace?.spaceName,
    isAddCustomerloading: createCustomerResponse.isLoading,
    isLoading:
      fetchCFAsListResponse?.isLoading || fetchCFAsListResponse?.isFetching,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
    isDisable: cfaPayload?.length === 0,
    handleBack,
  };
};
